import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Building2, Users, Plus, Minus, Info } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import confetti from "canvas-confetti";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONAT_OPTIONS = [
  { value: "1", label: "Januar" },
  { value: "2", label: "Februar" },
  { value: "3", label: "März" },
  { value: "4", label: "April" },
  { value: "5", label: "Mai" },
  { value: "6", label: "Juni" },
  { value: "7", label: "Juli" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "Oktober" },
  { value: "11", label: "November" },
  { value: "12", label: "Dezember" },
];

export default function FirmenanmeldungPage() {
  const [firmenname, setFirmenname] = useState("");
  const [rechnungsadresse, setRechnungsadresse] = useState("");
  const [rechnungsEmail, setRechnungsEmail] = useState("");
  const [ansprechpartner, setAnsprechpartner] = useState("");
  const [teilnehmerAnzahl, setTeilnehmerAnzahl] = useState(1);
  const [teilnehmer, setTeilnehmer] = useState([createEmptyTeilnehmer()]);
  const [datenschutz, setDatenschutz] = useState(false);
  const [haftung, setHaftung] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  function createEmptyTeilnehmer() {
    return {
      vorname: "",
      nachname: "",
      geburtsdatum_tag: "",
      geburtsdatum_monat: "",
      geburtsdatum_jahr: "",
      email: "",
    };
  }

  const handleAnzahlChange = (newAnzahl) => {
    const anzahl = Math.max(1, Math.min(50, parseInt(newAnzahl) || 1));
    setTeilnehmerAnzahl(anzahl);
    
    // Adjust teilnehmer array
    if (anzahl > teilnehmer.length) {
      const newTeilnehmer = [...teilnehmer];
      for (let i = teilnehmer.length; i < anzahl; i++) {
        newTeilnehmer.push(createEmptyTeilnehmer());
      }
      setTeilnehmer(newTeilnehmer);
    } else if (anzahl < teilnehmer.length) {
      setTeilnehmer(teilnehmer.slice(0, anzahl));
    }
  };

  const updateTeilnehmer = (index, field, value) => {
    const updated = [...teilnehmer];
    updated[index] = { ...updated[index], [field]: value };
    setTeilnehmer(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!firmenname.trim()) {
      toast.error("Bitte geben Sie den Firmennamen ein.");
      return;
    }
    if (!rechnungsadresse.trim()) {
      toast.error("Bitte geben Sie die Rechnungsadresse ein.");
      return;
    }
    if (!rechnungsEmail.trim()) {
      toast.error("Bitte geben Sie die Rechnungs-E-Mail ein.");
      return;
    }
    if (!datenschutz || !haftung) {
      toast.error("Bitte bestätigen Sie die rechtlichen Hinweise.");
      return;
    }

    // Validate at least first participant has name
    if (!teilnehmer[0].vorname || !teilnehmer[0].nachname) {
      toast.error("Bitte geben Sie mindestens den ersten Teilnehmer vollständig ein.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/company-registrations`, {
        firmenname: firmenname.trim(),
        rechnungsadresse: rechnungsadresse.trim(),
        rechnungs_email: rechnungsEmail.trim(),
        ansprechpartner_buchhaltung: ansprechpartner.trim(),
        teilnehmer: teilnehmer.filter(t => t.vorname && t.nachname),
        datenschutz_akzeptiert: datenschutz,
        haftungsausschluss_akzeptiert: haftung,
      });
      
      setSuccess(true);
      toast.success("Firmenanmeldung erfolgreich übermittelt!");
      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 }
      });
    } catch (err) {
      toast.error("Fehler bei der Anmeldung. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ background: "linear-gradient(135deg, #f6eef7 0%, #e8f7f0 100%)" }}>
        <Card className="max-w-lg w-full border-0 shadow-xl text-center">
          <CardContent className="p-10">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: "rgba(149, 225, 187, 0.2)" }}>
              <CheckCircle2 className="w-10 h-10" style={{ color: "#059669" }} />
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold mb-4" style={{ color: "#1F2937" }}>
              Vielen Dank für eure Firmenanmeldung zum Run for Rett 💜
            </h1>
            <p className="text-base mb-6" style={{ color: "#4B5563" }}>
              Die Anmeldung wurde erfolgreich übermittelt.
            </p>
            <p className="text-sm mb-8" style={{ color: "#6B7280" }}>
              Alle eingetragenen Teilnehmer mit E-Mail-Adresse erhalten eine Bestätigung.
            </p>
            <Link to="/">
              <Button className="rounded-full px-8 py-5 font-bold bg-[#b46eb6] hover:bg-[#8f5691] text-white">
                Zur Hauptseite
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "linear-gradient(135deg, #f6eef7 0%, #e8f7f0 100%)" }}>
      {/* Decorative Background Images */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        {/* Top Left */}
        <div className="absolute top-24 left-8 w-64 h-52 opacity-[0.15] rounded-2xl overflow-hidden rotate-[-12deg]">
          <img src="/assets/firmen-bg-1.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        {/* Top Right */}
        <div className="absolute top-20 right-8 w-72 h-56 opacity-[0.12] rounded-2xl overflow-hidden rotate-[10deg]">
          <img src="/assets/firmen-bg-2.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        {/* Middle Left */}
        <div className="absolute top-[45%] left-6 w-60 h-48 opacity-[0.12] rounded-2xl overflow-hidden rotate-[-6deg]">
          <img src="/assets/firmen-bg-3.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        {/* Middle Right */}
        <div className="absolute top-[42%] right-10 w-64 h-52 opacity-[0.13] rounded-2xl overflow-hidden rotate-[8deg]">
          <img src="/assets/firmen-bg-6.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        {/* Bottom Left */}
        <div className="absolute bottom-48 left-10 w-56 h-44 opacity-[0.14] rounded-2xl overflow-hidden rotate-[-8deg]">
          <img src="/assets/firmen-bg-4.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        {/* Bottom Right */}
        <div className="absolute bottom-32 right-6 w-60 h-48 opacity-[0.12] rounded-2xl overflow-hidden rotate-[6deg]">
          <img src="/assets/firmen-bg-5.jpg" alt="" className="w-full h-full object-cover" />
        </div>
      </div>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo-runforrett.svg" alt="Run for Rett Logo" className="h-10 w-auto" />
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-sm" style={{ color: "#b46eb6" }}>
              Zur Hauptseite
            </Button>
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-12 md:py-16 relative z-10">
        {/* Hero Section */}
        <div className="text-center mb-10 md:mb-14">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ background: "rgba(180, 110, 182, 0.15)" }}>
            <Building2 className="w-8 h-8" style={{ color: "#b46eb6" }} />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4" style={{ color: "#1F2937" }}>
            Firmenanmeldung – Run for Rett
          </h1>
          <p className="text-base md:text-lg max-w-2xl mx-auto" style={{ color: "#4B5563" }}>
            Unternehmen können hier mehrere Mitarbeitende gesammelt zum Run for Rett anmelden und gemeinsam ein Zeichen für Bewegung, Teamgeist und Forschung setzen.
          </p>
        </div>

        {/* Info Box */}
        <Card className="border-0 shadow-lg mb-8 overflow-hidden" style={{ background: "rgba(180, 110, 182, 0.05)" }}>
          <CardContent className="p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(180, 110, 182, 0.15)" }}>
                <Info className="w-5 h-5" style={{ color: "#b46eb6" }} />
              </div>
              <div className="space-y-3">
                <h3 className="font-heading font-bold text-lg" style={{ color: "#1F2937" }}>
                  Informationen zur Firmenanmeldung
                </h3>
                <div className="space-y-3 text-sm" style={{ color: "#4B5563", lineHeight: "1.7" }}>
                  <p>
                    Unternehmen haben beim Run for Rett die Möglichkeit, mehrere Mitarbeitende gesammelt anzumelden und gemeinsam als Team am Lauf teilzunehmen. Die Anmeldung erfolgt über dieses Formular durch eine verantwortliche Person aus dem Unternehmen.
                  </p>
                  <p>
                    Bitte gebt die Anzahl der Teilnehmenden sowie die entsprechenden Personendaten vollständig an. Die einzelnen Teilnehmer werden anschließend automatisch im System registriert.
                  </p>
                  <p className="text-lg md:text-xl font-bold" style={{ color: "#b46eb6" }}>
                    Für Firmenläufer wird eine Startgebühr von 25 € pro Person erhoben.
                  </p>
                  <p>
                    Nach erfolgreicher Anmeldung wird die Gesamtrechnung an die angegebene Firma übermittelt.
                  </p>
                  <p>
                    Die Teilnahmegebühr unterstützt direkt die Organisation des Spendenlaufs sowie die Förderung der Forschung zum Rett-Syndrom.
                  </p>
                  <p>
                    Alle angemeldeten Personen erhalten – sofern eine E-Mail-Adresse angegeben wurde – eine kurze Information darüber, dass sie von ihrer Firma für den Run for Rett registriert wurden.
                  </p>
                  <p className="font-medium" style={{ color: "#1F2937" }}>
                    Wir freuen uns sehr über jedes Unternehmen, das sich gemeinsam mit seinen Mitarbeitenden für diesen guten Zweck engagiert.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form */}
        <Card className="border-0 shadow-xl overflow-hidden">
          <div className="h-1.5 w-full" style={{ background: "linear-gradient(90deg, #b46eb6, #7DD4B5)" }} />
          <CardContent className="p-6 md:p-10">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Firmeninformationen */}
              <div className="space-y-6">
                <h2 className="font-heading text-xl font-bold flex items-center gap-2" style={{ color: "#1F2937" }}>
                  <Building2 className="w-5 h-5" style={{ color: "#b46eb6" }} />
                  Firmeninformationen
                </h2>
                
                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label className="font-semibold text-sm" style={{ color: "#1F2937" }}>
                      Wie heißt eure Firma? *
                    </Label>
                    <Input
                      data-testid="firmenname-input"
                      value={firmenname}
                      onChange={(e) => setFirmenname(e.target.value)}
                      placeholder="Firmenname eingeben"
                      className="rounded-lg border-gray-200 bg-gray-50 h-11"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="font-semibold text-sm" style={{ color: "#1F2937" }}>
                      Rechnungsadresse *
                    </Label>
                    <Textarea
                      data-testid="rechnungsadresse-input"
                      value={rechnungsadresse}
                      onChange={(e) => setRechnungsadresse(e.target.value)}
                      placeholder="Straße, Hausnummer, PLZ, Ort"
                      className="rounded-lg border-gray-200 bg-gray-50 min-h-[80px]"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label className="font-semibold text-sm" style={{ color: "#1F2937" }}>
                        Rechnungs-E-Mail *
                      </Label>
                      <Input
                        data-testid="rechnungs-email-input"
                        type="email"
                        value={rechnungsEmail}
                        onChange={(e) => setRechnungsEmail(e.target.value)}
                        placeholder="rechnung@firma.de"
                        className="rounded-lg border-gray-200 bg-gray-50 h-11"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="font-semibold text-sm" style={{ color: "#1F2937" }}>
                        Ansprechpartner Buchhaltung
                      </Label>
                      <Input
                        data-testid="ansprechpartner-input"
                        value={ansprechpartner}
                        onChange={(e) => setAnsprechpartner(e.target.value)}
                        placeholder="Name des Ansprechpartners"
                        className="rounded-lg border-gray-200 bg-gray-50 h-11"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="font-semibold text-sm" style={{ color: "#1F2937" }}>
                    Wie viele Personen sollen angemeldet werden? *
                  </Label>
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => handleAnzahlChange(teilnehmerAnzahl - 1)}
                      disabled={teilnehmerAnzahl <= 1}
                      className="h-11 w-11 rounded-lg"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <Input
                      data-testid="anzahl-input"
                      type="number"
                      min="1"
                      max="50"
                      value={teilnehmerAnzahl}
                      onChange={(e) => handleAnzahlChange(e.target.value)}
                      className="rounded-lg border-gray-200 bg-gray-50 h-11 w-24 text-center"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => handleAnzahlChange(teilnehmerAnzahl + 1)}
                      disabled={teilnehmerAnzahl >= 50}
                      className="h-11 w-11 rounded-lg"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                    <span className="text-sm" style={{ color: "#6B7280" }}>
                      (max. 50 Personen)
                    </span>
                  </div>
                </div>
              </div>

              {/* Teilnehmer */}
              <div className="space-y-6">
                <h2 className="font-heading text-xl font-bold flex items-center gap-2" style={{ color: "#1F2937" }}>
                  <Users className="w-5 h-5" style={{ color: "#b46eb6" }} />
                  Teilnehmer ({teilnehmerAnzahl})
                </h2>

                <div className="space-y-6">
                  {teilnehmer.map((t, idx) => (
                    <div key={idx} className="p-4 md:p-5 rounded-xl border border-gray-100 bg-gray-50/50">
                      <p className="font-heading font-bold text-sm mb-4" style={{ color: "#b46eb6" }}>
                        #{idx + 1}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-1">
                          <Label className="text-xs font-medium" style={{ color: "#6B7280" }}>Vorname</Label>
                          <Input
                            data-testid={`teilnehmer-${idx}-vorname`}
                            value={t.vorname}
                            onChange={(e) => updateTeilnehmer(idx, "vorname", e.target.value)}
                            placeholder="Vorname"
                            className="rounded-lg border-gray-200 bg-white h-10 text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs font-medium" style={{ color: "#6B7280" }}>Nachname</Label>
                          <Input
                            data-testid={`teilnehmer-${idx}-nachname`}
                            value={t.nachname}
                            onChange={(e) => updateTeilnehmer(idx, "nachname", e.target.value)}
                            placeholder="Nachname"
                            className="rounded-lg border-gray-200 bg-white h-10 text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs font-medium" style={{ color: "#6B7280" }}>Geburtsdatum</Label>
                          <div className="flex gap-1">
                            <Input
                              data-testid={`teilnehmer-${idx}-tag`}
                              value={t.geburtsdatum_tag}
                              onChange={(e) => updateTeilnehmer(idx, "geburtsdatum_tag", e.target.value)}
                              placeholder="TT"
                              maxLength={2}
                              className="rounded-lg border-gray-200 bg-white h-10 text-sm w-12 text-center px-1"
                            />
                            <Select value={t.geburtsdatum_monat} onValueChange={(v) => updateTeilnehmer(idx, "geburtsdatum_monat", v)}>
                              <SelectTrigger className="rounded-lg border-gray-200 bg-white h-10 text-sm flex-1 min-w-0">
                                <SelectValue placeholder="MM" />
                              </SelectTrigger>
                              <SelectContent>
                                {MONAT_OPTIONS.map((m) => (
                                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              data-testid={`teilnehmer-${idx}-jahr`}
                              value={t.geburtsdatum_jahr}
                              onChange={(e) => updateTeilnehmer(idx, "geburtsdatum_jahr", e.target.value)}
                              placeholder="JJJJ"
                              maxLength={4}
                              className="rounded-lg border-gray-200 bg-white h-10 text-sm w-16 text-center px-1"
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs font-medium" style={{ color: "#6B7280" }}>E-Mail</Label>
                          <Input
                            data-testid={`teilnehmer-${idx}-email`}
                            type="email"
                            value={t.email}
                            onChange={(e) => updateTeilnehmer(idx, "email", e.target.value)}
                            placeholder="E-Mail-Adresse"
                            className="rounded-lg border-gray-200 bg-white h-10 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Rechtliche Bestätigungen */}
              <div className="space-y-5 p-5 rounded-xl" style={{ background: "rgba(180, 110, 182, 0.05)" }}>
                <h3 className="font-heading font-bold text-sm" style={{ color: "#1F2937" }}>
                  Rechtliche Bestätigungen
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="datenschutz"
                      data-testid="datenschutz-checkbox"
                      checked={datenschutz}
                      onCheckedChange={setDatenschutz}
                      className="mt-0.5"
                    />
                    <label htmlFor="datenschutz" className="text-sm cursor-pointer" style={{ color: "#4B5563" }}>
                      Ich habe die <Link to="/datenschutz" className="underline" style={{ color: "#b46eb6" }}>Datenschutzerklärung</Link> gelesen und akzeptiert. Ich bestätige, dass ich mit der Verarbeitung meiner Daten einverstanden bin.
                    </label>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="haftung"
                      data-testid="haftung-checkbox"
                      checked={haftung}
                      onCheckedChange={setHaftung}
                      className="mt-0.5"
                    />
                    <label htmlFor="haftung" className="text-sm cursor-pointer" style={{ color: "#4B5563" }}>
                      Ich erkenne den Haftungsausschluss der Veranstalter an. Ich erkläre, auf eigene Verantwortung an der Veranstaltung teilzunehmen.
                    </label>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                data-testid="submit-company-btn"
                disabled={loading}
                className="w-full rounded-full py-6 text-lg font-bold bg-[#b46eb6] hover:bg-[#8f5691] text-white shadow-lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Wird übermittelt...
                  </>
                ) : (
                  "Firmenanmeldung einreichen"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer hint */}
        <p className="text-center text-xs mt-8" style={{ color: "#9CA3AF" }}>
          Diese Seite ist ausschließlich für Firmenanmeldungen vorgesehen.
        </p>
      </div>
    </div>
  );
}
